import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { getCallerSub } from '@/identity.js';
import {
  ApiError,
  fetchSkillRevision,
  fetchSkillRevisions,
  restoreSkillRevision,
  skillRevisionsKey,
  type Skill,
  type SkillRevisionSummary,
} from '@/lib/api/skills.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';
import { SkillContentPreview } from './SkillContentPreview.js';
import { SkillDraftNote } from './SkillDraftNote.js';
import { SkillRevisionChanges } from './SkillRevisionChanges.js';
import { relativeTime } from '@/ui/chat/relative-time.js';

/** Shown when the skill moved on before a restore or a card save, after which the latest version is shown. */
export const SKILL_CHANGED_MESSAGE =
  'This skill changed meanwhile. The latest version is shown now.';

/** A skill row from a service with revisions: only such a row has versions to show. */
export type VersionedSkill = Skill & { revision: number };

/** Whether the row comes from a service with revisions, which always sends `revision`. */
export function hasVersions(skill: Skill): skill is VersionedSkill {
  return typeof skill.revision === 'number';
}

const ORIGIN_LABELS: Record<string, string> = {
  created: 'Created',
  edit: 'Edited',
  resource: 'Files changed',
  chat: 'Saved from chat',
  restore: 'Restored',
  admin: 'Updated by admin',
  import: 'Imported',
};

/** Label for a revision origin. An origin this client does not know still renders. */
function originLabel(origin: string): string {
  return ORIGIN_LABELS[origin] ?? 'Changed';
}

/**
 * "You" for the caller's own revisions, "Admin" for one nobody signed (an admin update), otherwise
 * the author's name, or a neutral label when the service has none.
 */
function authorLabel(entry: SkillRevisionSummary): string {
  if (entry.author_user_id === null) return 'Admin';
  if (entry.author_user_id === getCallerSub()) return 'You';
  return entry.author_name ?? 'Another editor';
}

/** One revision's content. It never changes, so it is cached for good. */
function revisionQueryOptions(skillId: string, revision: number) {
  return {
    queryKey: ['skills', 'revisions', skillId, revision],
    queryFn: () => fetchSkillRevision(skillId, revision),
    staleTime: Infinity,
  };
}

/** "No files", "1 file" or "N files". */
function fileCountLabel(fileCount: number): string {
  if (fileCount === 0) return 'No files';
  return fileCount === 1 ? '1 file' : `${fileCount} files`;
}

/**
 * The version history of one skill inside its card: a collapsed heading with the current number,
 * the revisions newest first, a read-only look at any of them, and Restore for the older ones.
 * Also carries the "unsaved changes from chat" note for a company skill, the one scope whose chat
 * changes wait as the caller's own pending version; a private or workspace skill changes at once.
 *
 * A restore sends the head this card showed as `expected_revision`, so nobody restores over a change
 * they never saw.
 */
export function SkillVersions({
  skill,
}: {
  skill: VersionedSkill;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  return (
    <div className="flex flex-col gap-1.5">
      {skill.has_draft && skill.scope === 'corporate' && (
        <SkillDraftNote skillId={skill.id} />
      )}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="flex items-center gap-1 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        <Chevron className="size-3.5" aria-hidden="true" />
        Versions
        <span className="font-normal normal-case tracking-normal">
          (current: {skill.revision})
        </span>
      </button>
      {open && <RevisionList skill={skill} />}
    </div>
  );
}

function RevisionList({ skill }: { skill: VersionedSkill }): JSX.Element {
  const [expanded, setExpanded] = useState<number | null>(null);
  const history = useQuery({
    queryKey: skillRevisionsKey(skill.id),
    queryFn: () => fetchSkillRevisions(skill.id),
  });
  if (history.isError) {
    return (
      <p className="text-xs text-muted-foreground">
        Could not load the versions.
      </p>
    );
  }
  if (!history.data) return <Skeleton className="h-16 w-full rounded-lg" />;
  return (
    <ul className="flex flex-col gap-1">
      {history.data.items.map((entry) => (
        <RevisionRow
          key={entry.revision}
          skill={skill}
          entry={entry}
          isHead={entry.revision === skill.revision}
          isExpanded={expanded === entry.revision}
          onToggle={() =>
            setExpanded(expanded === entry.revision ? null : entry.revision)
          }
        />
      ))}
    </ul>
  );
}

function RevisionRow({
  skill,
  entry,
  isHead,
  isExpanded,
  onToggle,
}: {
  skill: VersionedSkill;
  entry: SkillRevisionSummary;
  isHead: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <li className="rounded-lg border border-border/40">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-label={`Version ${entry.revision}`}
        onClick={onToggle}
        className={cn(
          'flex w-full flex-wrap items-center gap-x-2 px-3 py-2 text-left text-xs',
          isExpanded && 'bg-muted/20',
        )}
      >
        <span className="font-medium text-foreground">#{entry.revision}</span>
        <span className="text-foreground">{originLabel(entry.origin)}</span>
        {isHead && (
          <span className="rounded-full bg-primary/10 px-1.5 text-[10px] font-medium text-primary">
            Current
          </span>
        )}
        <span className="text-muted-foreground">
          {authorLabel(entry)} ·{' '}
          {relativeTime(new Date(entry.created_at).getTime())}
          {typeof entry.file_count === 'number' &&
            ` · ${fileCountLabel(entry.file_count)}`}
        </span>
      </button>
      {isExpanded && (
        <RevisionBody skill={skill} revision={entry.revision} isHead={isHead} />
      )}
    </li>
  );
}

/**
 * The read-only instructions of one revision, what it changed against the revision before it, and
 * Restore, which the current version does not offer.
 */
function RevisionBody({
  skill,
  revision,
  isHead,
}: {
  skill: VersionedSkill;
  revision: number;
  isHead: boolean;
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();
  const detail = useQuery(revisionQueryOptions(skill.id, revision));
  const previous = useQuery({
    ...revisionQueryOptions(skill.id, revision - 1),
    enabled: revision > 1,
  });
  const restore = useMutation({
    mutationFn: () => restoreSkillRevision(skill.id, revision, skill.revision),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['skills'] });
      notify.success(`Restored version ${revision}`);
    },
    onError: (error) => {
      setConfirming(false);
      if (error instanceof ApiError && error.status === 409) {
        notify.error(SKILL_CHANGED_MESSAGE);
        void queryClient.invalidateQueries({ queryKey: ['skills'] });
      }
    },
  });
  return (
    <div className="flex flex-col gap-2 border-t border-border/40 p-3">
      {detail.data ? (
        <SkillContentPreview
          instructions={detail.data.instructions}
          resources={detail.data.resources}
        />
      ) : (
        <Skeleton className="h-12 w-full rounded-lg" />
      )}
      {detail.data && (revision === 1 || previous.data) && (
        <SkillRevisionChanges
          before={revision === 1 ? null : (previous.data ?? null)}
          after={detail.data}
        />
      )}
      {!isHead && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={!detail.data}
            onClick={() => setConfirming(true)}
          >
            Restore this version
          </Button>
        </div>
      )}
      <AppDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Restore this version?"
        description="The current version stays in the history."
      >
        <div className="mt-3 flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={restore.isPending}
            onClick={() => restore.mutate()}
          >
            Restore
          </Button>
        </div>
      </AppDialog>
    </div>
  );
}
